import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import api from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ---------- GLOBAL TOAST ----------
const showToast = (type, message) => {
  const options = {
    position: "top-right",
    autoClose: 2000,
    theme: "colored",
  };

  switch (type) {
    case "success":
      toast.success(message, options);
      break;
    case "error":
      toast.error(message, options);
      break;
    case "warning":
      toast.warning(message, options);
      break;
    default:
      toast.info(message, options);
  }
};

const ClassSectionPage = () => {
  const [form, setForm] = useState({ class_name: "" });
  const [sectionForm, setSectionForm] = useState({ section_name: "", class_id: "" });

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);

  const [editClassId, setEditClassId] = useState(null);
  const [editSectionId, setEditSectionId] = useState(null);

  const schoolId = localStorage.getItem("school_id");
  const baseURL = "/classes";

  // ================= FETCH CLASSES =================
  const fetchClasses = async () => {
    try {
      const res = await api.get(`${baseURL}/list`, {
        headers: { "X-School-Id": schoolId },
      });
      setClasses(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      showToast("error", "Failed to load classes");
      setClasses([]);
    }
  };

  // ================= FETCH SECTIONS =================
  const fetchSections = async () => {
    try {
      const res = await api.get(`${baseURL}/list_sections`, {
        headers: { "X-School-Id": schoolId },
      });
      setSections(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      showToast("error", "Failed to load sections");
      setSections([]);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchSections();
  }, []);

  // ================= FORM CHANGE =================
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSectionChange = (e) =>
    setSectionForm({ ...sectionForm, [e.target.name]: e.target.value });

  // ================= UPDATE CLASS =================
  const handleSubmitClass = async (e) => {
    e.preventDefault();

    if (!form.class_name.trim()) return showToast("error", "Class name is required");

    if (!editClassId)
      return showToast("error", "Class add disabled (Fixed by plan)");

    try {
      const res = await api.post(`${baseURL}/update/${editClassId}`, form, {
        headers: { "X-School-Id": schoolId },
      });

      if (res.data?.status) {
        showToast("success", "Class updated successfully");
        setForm({ class_name: "" });
        setEditClassId(null);
        fetchClasses();
      }
    } catch {
      showToast("error", "Network/server error");
    }
  };

  // ================= ADD/UPDATE SECTION =================
  const handleSubmitSection = async (e) => {
    e.preventDefault();

    if (!sectionForm.class_id || !sectionForm.section_name.trim())
      return showToast("error", "Class & Section name required");

    try {
      let res;

      if (editSectionId) {
        res = await api.post(`${baseURL}/update_section/${editSectionId}`, sectionForm, {
          headers: { "X-School-Id": schoolId },
        });
      } else {
        res = await api.post(`${baseURL}/add_section`, sectionForm, {
          headers: { "X-School-Id": schoolId },
        });
      }

      if (res.data?.status) {
        showToast("success", editSectionId ? "Section updated" : "Section added");
        setSectionForm({ class_id: "", section_name: "" });
        setEditSectionId(null);
        fetchSections();
      }
    } catch {
      showToast("error", "Failed to save section");
    }
  };

  // ================= EDIT HANDLERS =================
  const handleEditClass = (item) => {
    setForm({ class_name: item.class_name });
    setEditClassId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEditSection = (item) => {
    setSectionForm({
      class_id: item.class_id,
      section_name: item.section_name,
    });
    setEditSectionId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ================= DELETE =================
  const handleDeleteClass = () => {
    showToast("error", "Class delete is disabled");
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm("Delete this section?")) return;

    try {
      const res = await api.delete(`${baseURL}/delete_section/${id}`, {
        headers: { "X-School-Id": schoolId },
      });

      if (res.data?.status) {
        showToast("success", "Section deleted");
        fetchSections();
      }
    } catch {
      showToast("error", "Delete failed");
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <ToastContainer />
      <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
        <main style={{ padding: "30px", maxWidth: "1000px", margin: "auto" }}>
          
          {/* ===== Class Edit Form ===== */}
          {editClassId && (
            <section style={cardStyle}>
              <h2 style={titleStyle}>✏️ Edit Class</h2>

              <form onSubmit={handleSubmitClass}>
                <FormGroup label="Class Name">
                  <input
                    type="text"
                    name="class_name"
                    value={form.class_name}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  />
                </FormGroup>

                <div style={{ textAlign: "right" }}>
                  <button type="submit" style={buttonStyle}>
                    Update Class
                  </button>
                </div>
              </form>
            </section>
          )}

          {/* ===== Add/Edit Section ===== */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>{editSectionId ? "✏️ Edit Section" : "➕ Add Section"}</h2>

            <form onSubmit={handleSubmitSection}>
              <FormGroup label="Select Class">
                <select
                  name="class_id"
                  value={sectionForm.class_id}
                  onChange={handleSectionChange}
                  style={fullWidthInput}
                  required
                >
                  <option value="">Select Class</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.class_name}
                    </option>
                  ))}
                </select>
              </FormGroup>

              <FormGroup label="Section Name">
                <input
                  type="text"
                  name="section_name"
                  value={sectionForm.section_name}
                  onChange={handleSectionChange}
                  style={fullWidthInput}
                  required
                />
              </FormGroup>

              <div style={{ textAlign: "right" }}>
                <button type="submit" style={buttonStyle}>
                  {editSectionId ? "Update Section" : "Add Section"}
                </button>
              </div>
            </form>
          </section>

          {/* ===== Class List ===== */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>📚 All Classes</h2>

            <ul style={{ listStyle: "none", padding: 0 }}>
              {classes.map((item) => (
                <li key={item.id} style={listItemStyle}>
                  <span>{item.class_name}</span>
                  <div>
                    <FaEdit onClick={() => handleEditClass(item)} style={editIconStyle} />
                    <FaTrash onClick={() => handleDeleteClass(item.id)} style={deleteIconStyle} />
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* ===== Section List ===== */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>📑 All Sections</h2>

            <ul style={{ listStyle: "none", padding: 0 }}>
              {sections.map((item) => (
                <li key={item.id} style={listItemStyle}>
                  <span>
                    {item.section_name} — <small>(Class: {item.class_name})</small>
                  </span>
                  <div>
                    <FaEdit onClick={() => handleEditSection(item)} style={editIconStyle} />
                    <FaTrash onClick={() => handleDeleteSection(item.id)} style={deleteIconStyle} />
                  </div>
                </li>
              ))}
            </ul>
          </section>

        </main>
      </div>
    </div>
  );
};

// ===== Styles =====
const FormGroup = ({ label, children }) => (
  <div style={{ marginBottom: "15px" }}>
    <label style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}>{label}</label>
    {children}
  </div>
);

const fullWidthInput = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "15px",
};

const buttonStyle = {
  padding: "10px 25px",
  backgroundColor: "#0077cc",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "15px",
  cursor: "pointer",
};

const cardStyle = {
  background: "#fff",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  marginBottom: "25px",
};

const titleStyle = {
  color: "#0077cc",
  marginBottom: "20px",
};

const listItemStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 15px",
  borderRadius: "8px",
  background: "#f8f9fa",
  marginBottom: "10px",
};

const editIconStyle = { color: "#0077cc", cursor: "pointer", marginRight: "15px" };
const deleteIconStyle = { color: "red", cursor: "pointer" };

export default ClassSectionPage;
